var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { css, jsx } from '@emotion/react';
import { Animation, ZapierLogo } from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { Fragment, useState } from 'react';
import { BREAKPOINT_UNIVERSAL_SIDEBAR_SHOW_COLLAPSED, BREAKPOINT_UNIVERSAL_SIDEBAR_SHOW_OPEN, skipLinkHref as skipLinkHrefDefault, UNIVERSAL_TOPBAR_CONDENSED_HEIGHT, UNIVERSAL_TOPBAR_HEIGHT, } from '../../../constants';
import { useUniversalNavContext } from '../../../hooks';
import SkipLink from '../../SkipLink';
import InAppTopbarLink from './InAppTopbarLink';
import MenuCollapseButton from '../../MenuCollapseButton';
import AccountActions from '../AccountActions';
import SearchForm from '../SearchForm';
import SearchToggler from './SearchToggler';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';
import { emitFullStoryEvent } from '../../../utils/fullStoryEvents';
import { baseReset, boxSizing } from '../../../../utils/style-mixins/reset';
const Styles = labelStyles('UniversalTopbar', {
    root: [
        Object.assign(Object.assign({}, boxSizing), { display: 'flex', justifyContent: 'center', backgroundColor: `var(--zds-background-weaker, #FFFDF9)`, position: 'relative', borderBottom: '1px solid var(--zds-stroke-weaker, #D7D3C9)', height: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT, '@media (min-width: 450px)': {
                height: UNIVERSAL_TOPBAR_HEIGHT,
                gap: '0',
            }, '& #universalnav-signup, & #back-to-app': {
                borderRadius: '40px',
                '&::before': {
                    borderRadius: '42px',
                },
                '& span': {
                    font: `var(--zds-body-3)`,
                    color: 'var(--zds-gray-warm-1, #f9f7f3)',
                    lineHeight: '24px',
                },
            } }),
    ],
    header: ({ shouldRenderSidebar }) => [
        css(Object.assign(Object.assign({}, boxSizing), { width: '100%', maxWidth: 'none', height: '100%', padding: shouldRenderSidebar
                ? '0 var(--zds-space-24, 24px) 0 var(--zds-space-8, 8px)'
                : '0 var(--zds-space-24, 24px)', display: 'grid', gridTemplateColumns: 'max-content auto max-content', alignItems: 'stretch', gap: '6px', backgroundColor: 'var(--zds-background-weaker, #FFFDF9)' })),
    ],
    group: css(Object.assign(Object.assign({}, boxSizing), { display: 'grid', gridAutoFlow: 'column', alignItems: 'center', justifySelf: 'flex-end', gap: '5px', '@media (min-width: 450px)': {
            gap: 'var(--zds-space-8, 8px)',
        }, 'a[data-color=brand]': {
            height: '32px !important',
        }, '.call-to-action-button a[data-color=tertiary], .call-to-action-button a[data-color=primary]': {
            padding: '0 var(--zds-space-12, 12px)',
            height: '36px !important',
            font: `var(--zds-body-2) !important`,
            '&:hover': {
                boxShadow: 'none',
            },
            '& > span': {
                padding: 0,
            },
            '&:focus::before': {
                borderColor: 'var(--zds-ui-primary-strongest, #503EBD) !important',
            },
        }, '.call-to-action-button a[data-color=primary]': {
            backgroundColor: 'var(--zds-ui-primary-stronger, #695BE8)',
            font: `var(--zds-body-3) !important`,
            '&:hover, &:focus': {
                backgroundColor: 'var(--zds-ui-primary-strongest, #503EBD)',
            },
            '&:focus-visible::before': {
                border: 'var(--zds-border-focus, 2px solid #695BE8) !important',
            },
        }, '.call-to-action-button a[data-color=tertiary]': {
            border: '1px solid var(--zds-stroke-weaker, #D7D3C9)',
            color: 'var(--zds-text-default, #413735)',
            backgroundColor: 'var(--zds-background-weaker, #FFFDF9) !important',
            '&:hover, &:focus': {
                color: 'var(--zds-text-default, #413735)',
                border: '1px solid var(--zds-stroke-weaker, #D7D3C9)',
                backgroundColor: 'var(--zds-background-default, #F9F7F3) !important',
            },
            '&:focus-visible::before': {
                border: 'var(--zds-border-focus, 2px solid #695BE8) !important',
            },
        } })),
    mainNavigationContent: [
        Object.assign(Object.assign({}, boxSizing), { display: 'flex', alignItems: 'stretch', marginLeft: 'var(--zds-space-12, 12px)' }),
    ],
    searchForm: (props, { isSearching }) => [
        Object.assign(Object.assign({}, boxSizing), { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: Animation.transitionValue, opacity: 0, pointerEvents: 'none', transform: 'scaleX(0)' }),
        isSearching &&
            withModifier('searching', {
                opacity: 1,
                pointerEvents: 'auto',
                transform: 'scaleX(1)',
            }),
        props.isSearchFullWidth &&
            withModifier('fullWidth', {
                backgroundColor: `var(--zds-background-weaker, #FFFDF9)`,
                padding: '0 10px',
            }),
    ],
    linkButton: css({
        '& span': Object.assign(Object.assign({}, boxSizing), { font: `var(--zds-body-1)`, fontWeight: 'normal', color: 'var(--zds-gray-warm-10, #201515)' }),
    }),
    searchToggler: ({ isSearching }) => [
        Object.assign(Object.assign({}, boxSizing), { font: `var(--zds-body-1)`, transition: Animation.transitionValue }),
        isSearching &&
            withModifier('hidden', {
                opacity: 0.2,
                pointerEvents: 'none',
            }),
    ],
    logoLinksWrapper: Object.assign(Object.assign({}, boxSizing), { position: 'relative', '> a': Object.assign({}, baseReset) }),
    skipLink: Object.assign(Object.assign({}, boxSizing), { position: 'absolute', top: '100%', whiteSpace: 'nowrap' }),
    mobileMenuToggle: Object.assign(Object.assign({}, boxSizing), { display: 'block', [`@media (min-width: ${BREAKPOINT_UNIVERSAL_SIDEBAR_SHOW_COLLAPSED}px)`]: {
            display: 'none',
        } }),
    forceMenuCollapseButton: Object.assign(Object.assign({}, boxSizing), { display: 'none', [`@media (min-width: ${BREAKPOINT_UNIVERSAL_SIDEBAR_SHOW_OPEN}px)`]: {
            display: 'block',
        } }),
});
const InAppTopbar = (_a) => {
    var _b, _c, _d, _e;
    var { isSearchFullWidth, logoAriaLabel = 'Go to the Zapier Homepage', logoHref = '/', onLogoClick = () => { }, selectedHref = '', shouldRenderLogo = true, size = 'medium', skipLinkHref = skipLinkHrefDefault, renderAccountMenu = (props) => (jsx(AccountActions, { selectedHref: props.selectedHref, shouldRenderSignIn: props.size !== 'small', shouldRenderSignUp: true, size: props.size })), renderNotifications = () => null, renderSearchForm = (props) => (jsx(SearchForm, { isSearching: props.isSearching, onClose: props.onClose, searchInputId: props.searchInputId })) } = _a, otherProps = __rest(_a, ["isSearchFullWidth", "logoAriaLabel", "logoHref", "onLogoClick", "selectedHref", "shouldRenderLogo", "size", "skipLinkHref", "renderAccountMenu", "renderNotifications", "renderSearchForm"]);
    const { isSidebarOpenedByTopbar, isSidebarForcedOpen, currentUrl, setIsSidebarForcedOpen, getFullyQualifiedURL, onToggleSidebarVisibility, shouldRenderCallToAction, shouldRenderSidebar, topNavData, shouldRenderUpgradeCTA, } = useUniversalNavContext();
    const props = Object.assign({ isSearchFullWidth,
        logoAriaLabel,
        logoHref,
        onLogoClick,
        renderAccountMenu,
        renderNotifications,
        renderSearchForm,
        selectedHref,
        shouldRenderLogo,
        size,
        skipLinkHref }, otherProps);
    const logoSize = {
        width: 105,
        height: 28,
    };
    const searchInputId = 'universal-nav-app-search';
    const [isSearching, setIsSearching] = useState(false);
    const onCloseSearch = () => {
        setIsSearching(false);
    };
    const searchForm = props.renderSearchForm({
        isSearching,
        searchInputId,
        onClose: onCloseSearch,
    });
    const callToActionUrl = getFullyQualifiedURL(`${(_b = topNavData.callToAction) === null || _b === void 0 ? void 0 : _b.href}?demo_source=cs_nav_header_${currentUrl}`) || '';
    const upgradeCTAUrl = getFullyQualifiedURL((_c = topNavData.upgradeCTA) === null || _c === void 0 ? void 0 : _c.href) || '';
    const toggleSidebarVisibilityButton = (() => {
        const buttonLabel = isSidebarForcedOpen ? 'Close Sidebar' : 'Open Sidebar';
        const buttonIcon = isSidebarForcedOpen ? 'formX' : 'navMenu';
        return (jsx(Fragment, null,
            jsx("div", { css: Styles.mobileMenuToggle },
                jsx(MenuCollapseButton, { ariaExpanded: isSidebarOpenedByTopbar, icon: isSidebarOpenedByTopbar ? 'formX' : 'navMenu', label: "Menu", onClick: onToggleSidebarVisibility })),
            jsx("div", { css: Styles.forceMenuCollapseButton, "data-zds": true },
                jsx(MenuCollapseButton, { ariaExpanded: isSidebarForcedOpen, icon: buttonIcon, label: buttonLabel, onClick: () => setIsSidebarForcedOpen(!isSidebarForcedOpen) }))));
    })();
    return (jsx("div", { css: Styles.root, "data-zds": true },
        jsx("div", { css: Styles.header({ shouldRenderSidebar }), "data-testid": "universalTopbar", onClick: props.onClick, "data-zds": true },
            jsx("div", { css: Styles.group, "data-zds": true },
                shouldRenderSidebar && toggleSidebarVisibilityButton,
                props.shouldRenderLogo && (jsx("header", { css: Styles.logoLinksWrapper },
                    jsx("a", { "aria-label": props.logoAriaLabel, href: getFullyQualifiedURL(props.logoHref), onClick: props.onLogoClick, "data-zds": true },
                        jsx(ZapierLogo, Object.assign({}, logoSize))),
                    jsx("div", { css: Styles.skipLink, "data-zds": true },
                        jsx(SkipLink, { href: props.skipLinkHref }))))),
            jsx("div", { css: Styles.mainNavigationContent, "data-zds": true }, jsx("div", { "aria-label": "Search apps", css: Styles.searchForm(props, { isSearching }), onBlur: () => {
                    setIsSearching(false);
                }, onFocus: () => {
                    setIsSearching(true);
                    emitUserInteractionEvent({
                        interaction_name: searchInputId,
                        event_action: 'in_view',
                        option_selected: 'Universal Nav Search opened',
                    });
                }, role: "form", "data-zds": true }, searchForm)),
            jsx("div", { "aria-label": "Manage your account and search apps", css: Styles.group, role: "form", "data-zds": true },
                !!searchForm && (jsx("div", { css: Styles.searchToggler({ isSearching }), "data-testid": "searchToggler", "data-zds": true },
                    jsx(SearchToggler, { id: searchInputId, isSearching: false, size: props.size }))), (_d = topNavData.linksInApp) === null || _d === void 0 ? void 0 :
                _d.map((link) => (jsx(InAppTopbarLink, { navigationItem: Object.assign(Object.assign({ variant: 'link' }, topNavData.callToAction), { href: link.href, text: size === 'medium' ? link.text : '' }), position: "actions-menu", size: props.size, isButton: false, icon: link.icon, ariaLabel: link.text, key: link.text }, size === 'medium' ? link.text : ''))),
                topNavData.callToAction &&
                    props.size !== 'small' &&
                    shouldRenderCallToAction && (jsx(InAppTopbarLink, { className: "call-to-action-button", navigationItem: Object.assign(Object.assign({ variant: 'link', color: 'tertiary' }, topNavData.callToAction), { href: callToActionUrl }), position: "actions-menu", isButton: true, buttonSize: "medium" }, topNavData.callToAction.text)),
                topNavData.upgradeCTA &&
                    props.size !== 'small' &&
                    shouldRenderUpgradeCTA && (jsx(InAppTopbarLink, { className: "call-to-action-button", navigationItem: Object.assign(Object.assign({ variant: 'link' }, topNavData.upgradeCTA), { href: upgradeCTAUrl, text: topNavData.upgradeCTA.text, color: topNavData.upgradeCTA.color }), buttonSize: "medium", position: "actions-menu", isButton: true, ariaLabel: topNavData.upgradeCTA.text, key: topNavData.upgradeCTA.text, onClick: () => {
                        var _a, _b;
                        const eventProperties = {
                            interaction_name: 'universal_topbar_upgrade_cta_button_clicked',
                            event_action: 'click',
                            option_selected: 'n/a',
                            experiment_flag: true,
                            experiment_name: 'revenue-growth-universal-nav-upgrade-cta',
                            interaction_raw_text: (_b = (_a = topNavData.upgradeCTA) === null || _a === void 0 ? void 0 : _a.text) !== null && _b !== void 0 ? _b : '',
                        };
                        emitUserInteractionEvent(eventProperties);
                        emitFullStoryEvent(eventProperties);
                    } }, (_e = topNavData.upgradeCTA) === null || _e === void 0 ? void 0 : _e.text)),
                props.renderAccountMenu(props)))));
};
export default InAppTopbar;
//# sourceMappingURL=InAppTopbar.js.map
// @zapier/universal-layout@12.0.37 (fb08e5a7f)
